<template>
  <div class="home">
    <div class="loading-container no-scroll" v-if="isLoading">
      <img src="/loader.svg" height="400" width="300" />
    </div>
    <div v-if="!isLoading">
      <TypeAhead
        :fetchUrl="typeaheadUrl"
        :inputedLocation="inputedLocation"
        :typeaheadBaseLink="typeaheadBaseLink"
        :lat="lat"
        :lng="lng"
        @fetchForLink="callFetchTracksFromLocation"
        @setTypeaheadUrl="setTypeaheadUrl"
        @callInputedTracks="callFetchInputedTracks"
      >
        <input
          type="text"
          placeholder="Type Your Location"
          v-model="inputedLocation"
          @keyup="setTypeaheadUrl"
          @keyup.enter="callFetchInputedTracks"
        />
        <button @click="callFetchInputedTracks">Find Music!</button>
      </TypeAhead>
      <div class="toggle-container">
        <div class="toggle-buttons">
          <button
            class="toggle no-styles"
            :class="{ active: showMap }"
            @click="toggleMap()"
          >
            <span class="iconmap"></span>
          </button>
          <button
            class="toggle no-styles"
            :class="{ active: showSettings }"
            @click="toggleProperty(showSettings, 'showSettings')"
          >
            <span class="iconequalizer12 boxypl"></span>
          </button>
        </div>
      </div>
      <section class="map">
        <MusicMap
          :geolocation="geolocation"
          :markers="mapMarkers"
          v-bind="{ lat, lng, city }"
          @fetchData="callFetchTracksFromLocation"
        />
      </section>
      <transition name="scroll">
        <p v-if="sortedTracks.length === 0">
          We couldn't find any tracks by artists in your location. Try dragging
          the map or inputting a different location.
        </p>

        <div
          id="playlist"
          v-if="!showMap"
          v-infinite:callback="{
            fn: this.fetchTracksFromLocation,
            nestedEl: '.card',
            numPerRow: 5
          }"
        >
          <div class="exact-location">
            <span class="icontarget"></span>
            {{ city }}
          </div>
          <SpotifyButton
            v-if="sortedTracks.length > 0"
            :tracks="sortedTracks"
            :showPlayer="showPlayer"
            @toggleProperty="toggleProperty"
            :name="`${city} Top Tracks MWYA -- ${genreForTitle}`"
          />

          <ul v-if="sortedTracks.length > 0" class="cards">
            <MusicCard
              v-for="(a, index) in sortedTracks"
              :track="a"
              :name="a.name"
              :artists="a.artists"
              :id="a.id"
              :key="a.idindex"
              :index="index"
              @remove="remove"
              @getInfo="getInfo"
            />
          </ul>
        </div>
      </transition>
      <transition name="scroll-half">
        <div id="genres" v-if="showSettings">
          <div
            class="checkbox"
            v-for="(icon, index) in genreIcons"
            :key="icon.genre.genre"
          >
            <router-link
              :to="
                selectedGenres && selectedGenres.length
                  ? icon.genre.checkedState
                    ? `/${lat},${lng}/${selectedGenres
                        .replace(`*${icon.genre.genre}`, '')
                        .replace(`${icon.genre.genre}*`, '')
                        .replace(icon.genre.genre, '')}`
                    : `/${lat},${lng}/${icon.genre.genre}*${selectedGenres}`
                  : `/${lat},${lng}/${icon.genre.genre}`
              "
            >
              <input
                :checked="icon.genre.checkedState"
                type="checkbox"
                class="check"
              />
              <label
                :for="icon.genre.genre"
                :class="{ on: icon.genre.checkedState }"
                @click="setGenre(icon)"
              >
                <div :class="icon.genre.genre"></div>
                <div class="genre_text">{{ icon.genre.name }}</div>
              </label>
            </router-link>
          </div>
        </div>
      </transition>
      <transition name="scroll-from-top">
        <section id="info" v-if="showInfo">
          <!--  -->
          <GeneralInfo
            :artist="artist"
            :propertyToToggle="showInfo"
            :dataUrls="dataUrls"
            @toggleInfoIsResolved="toggleInfoIsResolved"
          >
            <template v-if="infoIsResolved" v-slot:default="slotProps">
              <div>
                <header>
                  <h2>{{ slotProps.data.bio.artist.name }}</h2>
                </header>
                <div class="container">
                  <section class="static-content">
                    <div class="image-bio">
                      <div
                        class="info-image-container"
                        v-if="
                          slotProps.data &&
                            slotProps.data.bio &&
                            slotProps.data.artist
                        "
                      >
                        <img
                          v-if="
                            slotProps.data &&
                              slotProps.data.bio.artist &&
                              slotProps.data.artist.images.length &&
                              slotProps.data.artist.images[0].url
                          "
                          :src="slotProps.data.artist.images[2].url"
                          :alt="slotProps.data.bio.artist.name"
                        />
                      </div>
                      <div
                        class="info-bio"
                        v-if="slotProps.data && slotProps.data.bio.artist.bio"
                      >
                        <p v-html="slotProps.data.bio.artist.bio.summary"></p>
                      </div>
                    </div>
                    <div class="info-related">
                      <h4>Related Artists</h4>
                      <ul
                        v-if="slotProps.data && slotProps.data.related.artists"
                      >
                        <li
                          class="related-artist-list-item"
                          v-for="r in slotProps.data.related.artists"
                          @click="slotProps.fetchData(r.name)"
                          :key="r.id"
                        >
                          <button
                            @click="getRelatedInfo(r)"
                            class="info-related-item"
                          >
                            <img
                              v-if="r.images[2]"
                              :src="r.images[2].url"
                              :alt="r.name"
                              height="160"
                              width="160"
                            />
                            <div>{{ r.name }}</div>
                          </button>
                        </li>
                      </ul>
                    </div>
                  </section>
                  <section class="media-content">
                    <SpotifyButton
                      v-if="
                        slotProps.data &&
                          slotProps.data.topTracks &&
                          slotProps.data.topTracks.tracks.length
                      "
                      :tracks="slotProps.data.topTracks.tracks"
                      :showPlayer="showInfoPlayer"
                      :property="'showInfoPlayer'"
                      @toggleProperty="toggleProperty"
                      :name="
                        `Top Tracks by ${
                          slotProps.data.topTracks.tracks[0].artists[0].name
                        } from MWYA `
                      "
                    />
                    <ul
                      v-if="
                        slotProps.data &&
                          slotProps.data.topTracks &&
                          slotProps.data.topTracks.tracks.length
                      "
                      class="top-tracks-list"
                    >
                      <li
                        class="top-tracks-item"
                        v-for="tt in slotProps.data.topTracks.tracks"
                        :key="tt.id"
                      >
                        <div>
                          <a
                            :href="'https://open.spotify.com/track/' + tt.id"
                            target="_blank"
                            >{{ tt.name }}</a
                          >
                        </div>
                      </li>
                    </ul>
                  </section>
                </div>
              </div>
              <transition name="fade-enter">
                <footer class="info-footer">
                  <div class="icon-container">
                    <button class="iconarrow-up" @click="closeInfo()"></button>
                  </div>
                </footer>
              </transition>
            </template>
          </GeneralInfo>
        </section>
      </transition>
    </div>
  </div>
</template>

<script>
// @ is an alias to /src
import TypeAhead from "@/components/TypeAhead.vue";
import MusicCard from "@/components/MusicCard.vue";
import MusicMap from "@/components/MusicMap.vue";
import GeneralInfo from "@/components/GeneralInfo.vue";
import SpotifyButton, { auth_header } from "@/components/SpotifyButton.vue";
import axios from "axios";
import { getGeolocation } from "@/utils/geolocation";
import { getSpotifyToken } from "@/utils/spotify-token";
import {
  sortArrAsc,
  flattenArrayOfObjects,
  getAllIndices,
  removeDuplicatesArrObj
} from "@/utils/arrays";
import { memoize } from "@/utils/memoize";
import {
  getGenres,
  getCheckedGenres,
  setCheckedGenre,
  getSimilarGenres,
  getGenreTitles,
  clearGenres,
  toggleGenre
} from "@/icons/genre-icons";

const isLocalHost = window.location.origin.indexOf("localhost") > -1;
const baseUrl = isLocalHost
  ? "https://localhost:3000/"
  : "https://api.musicwhereyouare.com/";

export default {
  name: "home",
  components: {
    MusicCard,
    MusicMap,
    GeneralInfo,
    SpotifyButton,
    TypeAhead
  },
  data() {
    return {
      topTracks: [],
      city: "",
      showSummary: true,
      geolocation: {},
      sortedTracks: [],
      inputedLocation: "",
      lat: 0,
      lng: 0,
      mapMarkers: [],
      showMap: false,
      lat_lng: "",
      showSettings: false,
      showInfo: false,
      showPlayer: false,
      showInfoPlayer: false,
      isLoading: false,
      genreIcons: getGenres(),
      genreForTitle: "",
      genres: [],
      page: -50,
      limit: 0,
      artist: {},
      dataUrls: {},
      cityList: [],
      typeaheadUrl: "",
      typeaheadBaseLink: "/",
      selectedGenres: "",
      infoIsResolved: false
    };
  },
  mounted: function() {
    const lat_lng =
      this.$route.params.lat_lng ||
      (sessionStorage.hash && window.location.hash !== ""
        ? sessionStorage.hash.replace("concerts/", "")
        : "");
    this.selectedGenres = this.$route.params.genres;
    if (this.selectedGenres) {
      this.selectedGenres.split("*").map(genre => setCheckedGenre(genre));
      this.setGenre();
    }
    if (lat_lng) {
      const latLng = lat_lng.split(",");
      if (parseInt(latLng[0].trim())) {
        const lat = latLng[0].trim();
        const lng = latLng[1].trim();
        this.fetchTracksFromLocation(lat, lng);
      }
    } else {
      this.fetchTracksFromCurrentGeolocation();
    }
  },
  methods: {
    fetchTracksFromLocation(lat, lng, page) {
      const router = this.$router;
      this.isLoading = true;
      if (page) {
        this.page = page;
        this.limit = this.page + 50;
      }
      if (!lat || !lng) {
        this.lat_lng = router.currentRoute.params.lat_lng;
        lat = this.lat_lng.split(",")[0];
        lng = this.lat_lng.split(",")[1];
      } else {
        this.lat_lng = `${lat},${lng}`;
      }
      if (!router.currentRoute.params.lat_lng) {
        router.push(this.lat_lng);
      }

      // sessionStorage.hash =
      //   this.selectedGenres && this.selectedGenres !== ""
      //     ? `${this.lat_lng}/${this.selectedGenres}`
      //     : this.lat_lng;
      const iterator = 50;
      this.page = this.page + iterator;
      this.limit = this.limit + iterator;
      const url = this.genres.length
        ? `${baseUrl}artistsMultiple/and~Lat:${lat}_Lng:${lng}_(or~${
            this.genres
          })?limit=${this.limit}&skip=${this.page}`
        : `${baseUrl}artistsLatLng/${lat}_${lng}?limit=${this.limit}&skip=${
            this.page
          }`;

      return memoize(url, axios.get, [url])
        .then(res => {
          let sortedData = sortArrAsc(
            flattenArrayOfObjects(
              res.data.filter(data => data.spotify.id),
              "spotify"
            ),
            "popularity",
            "num",
            false
          );
          const promises = sortedData.map(artist => {
            const baseUrl = `https://api.spotify.com/v1/artists/${
              artist.id
            }/top-tracks?market=US`;
            return memoize(baseUrl, fetch, [baseUrl], {
              headers: auth_header
            }).then(data => {
              const returnedData = data.clone().json();

              return returnedData;
            });
          });
          Promise.allSettled(promises).then(data => {
            if (
              data[0].value &&
              data[0].value.error &&
              data[0].value.error.status === 401
            ) {
              sessionStorage.removeItem("accessToken");
              getSpotifyToken();
            }
            let tracks = [];
            data.forEach((d, index) => {
              if (d && d.value && d.value.tracks) {
                d.value.Lat = res.data[index].Lat;
                d.value.Lng = res.data[index].Lng;
                d.value.City = res.data[index].City;
                d.value.Name = res.data[index].Name;
                tracks.push(d.value);
              }
            });
            // reorders tracks based on popularity
            const sortedTracks = removeDuplicatesArrObj(
              sortArrAsc(
                flattenArrayOfObjects(tracks, "tracks"),
                "popularity",
                "num",
                false
              ),
              "spotify.id"
            );

            const cities = res.data.map(loc => loc.City).sort();
            const citiesWCount = cities.map(city => {
              var obj = {};
              obj.city = city;
              obj.count = getAllIndices(cities, city);
              return obj;
            });
            const citiesSorted = citiesWCount.sort((a, b) => {
              return b.count - a.count;
            });
            this.city = citiesSorted[0].city;
            // this.inputedLocation = this.city;
            this.lat = res.data[0].Lat.$numberDecimal || res.data[0].Lat;
            this.lng = res.data[0].Lng.$numberDecimal || res.data[0].Lng;
            this.sortedTracks = this.sortedTracks.concat(sortedTracks);
            this.topTracks = this.topTracks.concat(tracks);
            this.mapMarkers = this.topTracks.reduce((accum, track) => {
              if (track && track.tracks && track.tracks.length > 0) {
                accum.push({
                  lat: track.Lat,
                  lng: track.Lng,
                  // title: `${track.City} <br> ${track.Name}`,
                  title: `<strong> ${track.City}</strong><br>${
                    track.Name
                  }<br>${track.tracks
                    .map(track => {
                      return `<a href="spotify:track:${track.id}">${
                        track.name
                      }</a>`;
                    })
                    .join("<br>")}`,
                  // ${track.artists
                  //   .map(artist => artist.name)
                  //   .join(", ")}</strong>`,
                  location: track.City
                });
              }
              return accum;
            }, []);
          });
          // needed for infinite scroll.  If undefined, infinite scroll will not continue to make calls
          return res.data;
        })
        .catch(() => {})
        .finally(() => {
          this.isLoading = false;
        });
    },

    fetchInputedTracks(city) {
      this.isLoading = true;
      this.clearGenres();
      this.page = -50;
      this.sortedTracks = [];
      this.topTracks = [];
      this.limit = 0;
      const location = city || this.inputedLocation;
      const url = `${baseUrl}topTracksMultiple/and~City:${location}?limit=1`;
      memoize(url, axios.get, [url])
        .then(res => {
          const city = res.data[0].City;
          const avg_lat =
            res.data.reduce((accum, lat) => {
              return accum + lat.Lat;
            }, 0) / res.data.length;
          const avg_lng =
            res.data.reduce((accum, lng) => {
              return accum + lng.Lng;
            }, 0) / res.data.length;

          this.fetchTracksFromLocation(avg_lat, avg_lng);
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    fetchTracksFromCurrentGeolocation() {
      getGeolocation().then(data => {
        const lat = data.latitude;
        const lng = data.longitude;
        Object.assign(this, {
          geolocation: data,
          lat,
          lng,
          lat_lng: `${lat},${lng}`
        });
        const router = this.$router;
        router.push(this.lat_lng);
        this.fetchTracksFromLocation(lat, lng);
      });
    },

    callFetchTracksFromLocation(lat, lng) {
      this.clearGenres();
      this.inputedLocation = "";
      if (!lat || !lng) {
        this.lat_lng = this.$router.currentRoute.params.lat_lng;
        const lat = this.lat_lng.split(",")[0];
        const lng = this.lat_lng.split(",")[1];
      }

      this.page = -50;
      this.topTracks = [];
      this.sortedTracks = [];
      this.limit = 0;

      this.fetchTracksFromLocation(lat, lng);
    },

    callFetchInputedTracks() {
      Object.assign(this, {
        showPlayer: false,
        isLoading: true,
        page: -50,
        sortedTracks: [],
        topTracks: [],
        limit: 0
      });
      this.clearGenres();
      const location = this.inputedLocation;

      location
        ? this.fetchInputedTracks(this.inputedLocation)
        : this.fetchTracksFromCurrentGeolocation();
    },

    getRelatedInfo(artist) {
      this.toggleInfoIsResolved();
      this.getInfo(artist);
    },

    getInfo(artist) {
      Object.assign(this, {
        showInfo: true,
        artist,
        dataUrls: {
          bio: `https://ws.audioscrobbler.com/2.0/?method=artist.getinfo&api_key=1f91c93293d618de5c30f8cfe2e9f5e9&format=json&artist=${
            artist.name
          }`,
          artist: `https://api.spotify.com/v1/artists/${artist.id}`,
          related: `https://api.spotify.com/v1/artists/${
            artist.id
          }/related-artists`,
          topTracks: `https://api.spotify.com/v1/artists/${
            artist.id
          }/top-tracks?market=US&limit=10`
        }
      });
    },

    remove: function(index) {
      this.sortedTracks.splice(index, 1);
    },

    toggleProperty: function(isTrue, property) {
      this[property] = !isTrue;
    },

    toggleMap() {
      this.toggleProperty(this.showMap, "showMap");
      this.showInfoPlayer = false;
    },

    toggleInfoIsResolved() {
      this.toggleProperty(this.infoIsResolved, "infoIsResolved");
    },

    closeInfo: function() {
      this.toggleProperty(this.showInfo, "showInfo");
      this.toggleInfoIsResolved();
      this.showInfoPlayer = false;
    },

    async setGenre(genre) {
      Object.assign(this, {
        page: -50,
        topTracks: [],
        sortedTracks: [],
        limit: 0
      });

      if (genre) {
        this.genreIcons = toggleGenre(genre);
      }

      const checkedGenres = getCheckedGenres();

      this.genreForTitle = getGenreTitles();

      const similarGenres = getSimilarGenres();
      this.selectedGenres = `${checkedGenres
        .map(genre => genre.genre.genre)
        .join("*")}`;

      const lat_lng = this.$route.params.lat_lng;
      const lat = lat_lng.split(",")[0];
      const lng = lat_lng.split(",")[1];
      this.genres = similarGenres;
      this.fetchTracksFromLocation(lat, lng);
    },

    clearGenres() {
      Object.assign(this, {
        genreIcons: clearGenres(),
        selectedGenres: null,
        genres: ""
      });
    },

    setTypeaheadUrl() {
      const inputedLocation = this.inputedLocation.toUpperCase().split(",");
      this.typeaheadUrl =
        inputedLocation.length > 1
          ? `${baseUrl}geolocationMultiple/and~City:${inputedLocation[0].trim()}_(or~Region:${inputedLocation[1].trim()})_(or~RegionId:${inputedLocation[1].trim()})`
          : `${baseUrl}geolocationMultiple/and~City:${inputedLocation[0].trim()}`;
    }
  },

  computed: {
    numTracks() {
      return this.sortedTracks.length;
    }
  }
};
</script>

<style>
.container {
  display: flex;
}
</style>
